import type { CostCenters } from "@/apis/gql/types";
import type { Props as CostCenterDropdownProps } from "@/components/shared/select-inputs/cost-center-dropdown";
import { ComboBox } from "@/components/shared/tables/tableComboBox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface CostCenterOptions {
  value: string;
  label: string;
  subLabel: string;
  alternativeLabels: string[];
}

type Props = {
  name: string;
  label?: string;
  options: CostCenters[];
  autoFocus?: boolean;
  placeholder: CostCenterDropdownProps["placeholder"];
  description?: string;
  fullWidth?: CostCenterDropdownProps["fullWidth"];
  className?: CostCenterDropdownProps["className"];
  containerClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
  direction?: "horizontal" | "vertical";
  sideOffset?: number;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
  variant?: "outline" | "ghost";
};

export const CostCenterDropdownInput = ({
  label,
  name,
  options: _options = [],
  fullWidth = true,
  placeholder,
  description,
  containerClassName,
  direction = "vertical",
  className,
  sideOffset,
  readOnly,
  disabled,
  variant = "outline",
}: Props) => {
  const form = useFormContext();
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const options = [
          ..._options.map(
            (costCenter, index): CostCenterOptions => ({
              label: costCenter.name,
              subLabel: costCenter.identifier?.toString(),
              alternativeLabels: [],
              value: costCenter.costCenterId,
            }),
          ),
          {
            label: t("cost_centers.no_cost_center"),
            subLabel: "0",
            alternativeLabels: [],
            value: "unknown",
          },
        ];

        // calculate the current value

        const currentOption = options.find((option) => {
          return option.value === field.value && field.value !== "unknown";
        });

        const value = currentOption
          ? {
              label: currentOption.label,
              value: currentOption.value,
              subLabel: currentOption.subLabel,
              alternativeLabels: [],
            }
          : undefined;

        return (
          <FormItem>
            <div
              className={cn(
                direction === "horizontal" &&
                  "flex flex-row items-center space-x-4",
                containerClassName,
              )}
            >
              {label && <FormLabel>{label}</FormLabel>}
              <FormControl>
                <ComboBox
                  currentOption={value}
                  items={options}
                  onChange={(value) => {
                    field.onChange(value === "unknown" ? null : value);
                  }}
                  emptyLabel={t("cost_centers.no_cost_center")}
                  className={className}
                  fullWidth={fullWidth}
                  placeholder={placeholder}
                  sideOffset={sideOffset}
                  variant={variant}
                  readOnly={readOnly}
                  disabled={disabled}
                />
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </div>
          </FormItem>
        );
      }}
    />
  );
};
